import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  CheckCircle2,
  ArrowRight,
  Phone,
  Target,
  Users,
  Map,
  Layers,
  ClipboardList,
  ListChecks,
  Route,
  Award,
  Upload
} from 'lucide-react';

export const GroundworkPackPage = () => {
  const problems = [
    'Unclear product-market fit across your SKU range',
    'Wrong country selection that wastes budget and time',
    'Starting dossiers before understanding regulator expectations',
    'Missing documents that surface as delays months later',
    'Rework from classification issues, such as pharma versus supplement'
  ];

  const deliverables = [
    {
      icon: Map,
      title: 'Country Selection Mapping',
      lede: 'We score your target markets so you enter the right ones first, in the right order.',
      bullets: [
        'Market-fit analysis across 30-plus countries',
        'Disease-burden and demand alignment',
        'Regulatory complexity scoring per country'
      ]
    },
    {
      icon: Layers,
      title: 'SKU Prioritization',
      lede: 'Not every product should launch first. We identify the ones that clear fastest.',
      bullets: [
        'Identify which SKUs are easiest and fastest to register',
        'Map formulations that match regulator acceptance',
        'Flag high-risk or non-permissible ingredients early'
      ]
    },
    {
      icon: ClipboardList,
      title: 'Regulatory Requirements Blueprint',
      lede: 'A clear picture of what each market expects, before you build anything.',
      bullets: [
        'Complete list of documents required for each market',
        'Country-by-country product classification',
        'Stability, artwork, and GMP/CPP expectations mapped per market'
      ]
    },
    {
      icon: ListChecks,
      title: 'Submission Readiness Checklist',
      lede: 'A clear yes or no on where you stand.',
      bullets: ['A yes/no readiness checklist for each SKU and each country']
    },
    {
      icon: Route,
      title: 'Roadmap for Next Steps',
      lede: 'Your sequenced plan to move forward with confidence.',
      bullets: [
        'Recommended launch sequence across selected markets',
        'Risk flags for regulatory or commercial exposure',
        'Packaging and labelling considerations ahead of dossier work'
      ]
    }
  ];

  const processSteps = [
    'Upload your product list',
    'We perform market and regulator-fit mapping',
    'We deliver your Groundwork Pack',
    'Q&A session with a regulatory expert to define next steps'
  ];

  const idealFor = [
    'First-time exporters',
    'Companies entering Africa, GCC, Asia or Latin America for the first time',
    'Manufacturers with a large SKU list needing prioritization',
    'Exporters unsure about product classification or allowed ingredients'
  ];

  const faqs = [
    {
      q: 'How do I decide which countries to export my pharma products to?',
      a: 'The Groundwork Pack scores your target markets on three factors: market fit and demand, disease-burden alignment, and regulatory complexity. You receive a ranked view across 30-plus countries, so you enter the markets that offer the best return for the least regulatory friction first.'
    },
    {
      q: 'What is the Groundwork Pack?',
      a: 'It is a pre-registration toolkit that gives pharmaceutical and nutraceutical exporters a country-specific roadmap for their top SKUs. It covers country selection, SKU prioritization, a regulatory requirements blueprint, a submission-readiness checklist, and a sequenced launch plan, all before any dossier work starts.'
    },
    {
      q: 'Which products should I launch first in a new market?',
      a: 'The pack prioritizes your SKUs by how easily and quickly they can be registered, maps formulations against regulator acceptance, and flags high-risk or non-permissible ingredients. This tells you which products to lead with and which to hold back.'
    },
    {
      q: 'Why do this before starting a dossier?',
      a: 'Starting a dossier before choosing the right market or product is the most common cause of costly rework. Groundwork ensures your effort goes into the right products and countries from the start, saving three to six months of avoidable delay.'
    },
    {
      q: 'Do you cover both pharmaceutical and nutraceutical products?',
      a: 'Yes. We assess both, and we keep the tracks separate, because product classification, allowed ingredients, and regulatory pathways differ between a drug and a supplement. Getting classification right at this stage prevents expensive reclassification later.'
    }
  ];

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Groundwork Pack',
    'alternateName': 'Pharma Export Market Selection & Country Entry Roadmap',
    'serviceType': 'Pharmaceutical Export Market Selection and Regulatory Roadmap',
    'description':
      'A pre-registration toolkit for pharmaceutical and nutraceutical exporters covering country selection mapping, SKU prioritization, a regulatory requirements blueprint, a submission-readiness checklist, and a sequenced launch roadmap for Africa, GCC, Asia and Latin America.',
    'provider': { '@type': 'Organization', 'name': 'TheApostrophe', 'url': 'https://www.theapostrophe.com' },
    'areaServed': ['Africa', 'GCC', 'Asia', 'Latin America'],
    'url': 'https://www.theapostrophe.com/success-packs/groundwork-pack',
    'offers': {
      '@type': 'Offer',
      'availability': 'https://schema.org/InStock',
      'url': 'https://www.theapostrophe.com/success-packs/groundwork-pack'
    }
  };

  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    'name': 'How the Groundwork Pack works',
    'description': 'A four-step process to decide which products to launch and which export markets to enter first.',
    'step': processSteps.map((s, i) => ({
      '@type': 'HowToStep',
      'position': i + 1,
      'name': `Step ${i + 1}`,
      'text': s
    }))
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map((f) => ({
      '@type': 'Question',
      'name': f.q,
      'acceptedAnswer': { '@type': 'Answer', 'text': f.a }
    }))
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://www.theapostrophe.com/' },
      { '@type': 'ListItem', 'position': 2, 'name': 'Success Packs', 'item': 'https://www.theapostrophe.com/success-packs' },
      { '@type': 'ListItem', 'position': 3, 'name': 'Groundwork Pack', 'item': 'https://www.theapostrophe.com/success-packs/groundwork-pack' }
    ]
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0E0D0' }}>
      <Helmet>
        <title>Pharma Export Market Selection & Country Entry Roadmap</title>
        <meta
          name="description"
          content="Decide which products to launch and which markets to enter first. Country selection, SKU prioritization and a regulatory roadmap for pharma and nutra exporters."
        />
        <meta
          name="keywords"
          content="pharma export market selection, country selection for pharma export, which countries to export pharma, SKU prioritization regulatory, market entry roadmap Africa Asia, regulatory complexity by country, product classification pharma vs supplement, pharma export planning, nutraceutical market entry"
        />
        <link rel="canonical" href="https://www.theapostrophe.com/success-packs/groundwork-pack" />

        <meta property="og:title" content="Pharma Export Market Selection & Country Entry Roadmap" />
        <meta property="og:description" content="Decide which products to launch and which markets to enter first. Country selection, SKU prioritization and a regulatory roadmap for pharma and nutra exporters." />
        <meta property="og:url" content="https://www.theapostrophe.com/success-packs/groundwork-pack" />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Pharma Export Market Selection & Country Entry Roadmap" />
        <meta name="twitter:description" content="Country selection, SKU prioritization and a sequenced launch roadmap for pharma & nutra exports." />

        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <Badge className="mb-6 px-6 py-2" style={{ backgroundColor: '#606090', color: 'white' }} data-testid="groundwork-pack-badge">
            Success Pack 2
          </Badge>
          <div className="w-24 h-24 rounded-2xl bg-white/60 flex items-center justify-center mb-6 mx-auto shadow-md">
            <Target className="h-12 w-12" style={{ color: '#606090' }} aria-hidden="true" />
          </div>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
            style={{ color: '#303040' }}
            data-testid="groundwork-pack-h1"
          >
            Choose the Right Export Markets Before You Start Dossiers
          </h1>
          <p className="text-xl md:text-2xl font-bold mb-8" style={{ color: '#606090' }}>
            Your regulatory foundation for entering Africa, GCC, Asia and Latin America, without guesswork.
          </p>
          <p className="text-base md:text-lg mb-10 max-w-3xl mx-auto leading-relaxed" style={{ color: '#606070' }}>
            Picking the wrong country or the wrong product to launch first is the most expensive mistake in export. It surfaces months later as rework, rejected classifications, and stalled dossiers. The Groundwork Pack gives you a country-specific, regulator-ready roadmap for your top SKUs, so you know exactly what to launch, where to launch it, and what you will need before any dossier work begins.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact#send-us-your-details">
              <Button
                size="lg"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: '#606090', color: 'white' }}
                data-testid="groundwork-build-btn"
              >
                Build My Groundwork Pack
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a
              href="https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true"
              target="_blank"
              rel="noopener noreferrer external"
              onClick={(e) => {
                e.preventDefault();
                window.open(
                  'https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true',
                  '_blank',
                  'noopener,noreferrer'
                );
              }}
            >
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ borderColor: '#606090', color: '#606090' }}
                data-testid="groundwork-expert-btn"
              >
                Talk to a Regulatory Expert
                <Phone className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* What It Solves */}
      <section className="py-20 px-6" style={{ backgroundColor: 'white' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center" style={{ color: '#303040' }}>
            What It Solves
          </h2>
          <p className="text-base md:text-lg text-center mb-12 max-w-3xl mx-auto" style={{ color: '#606070' }}>
            Most delays are decided long before submission, in the choices you make at the start. Common problems we remove:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {problems.map((p, i) => (
              <div
                key={i}
                className="flex items-start gap-3 p-6 rounded-xl transition-shadow hover:shadow-md"
                style={{ backgroundColor: '#F8F4EE' }}
              >
                <div
                  className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center font-bold text-white text-sm"
                  style={{ backgroundColor: '#606090' }}
                  aria-hidden="true"
                >
                  {i + 1}
                </div>
                <span className="text-base md:text-lg" style={{ color: '#505060' }}>
                  {p}
                </span>
              </div>
            ))}
          </div>
          <p className="text-base md:text-lg text-center mt-12 max-w-3xl mx-auto italic" style={{ color: '#606070' }}>
            The Groundwork Pack removes this uncertainty and gives you a clear compliance direction for fast-moving markets.
          </p>
        </div>
      </section>

      {/* What You Get - Deliverables */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center" style={{ color: '#303040' }}>
            What You Get
          </h2>
          <p className="text-base md:text-lg text-center mb-16 max-w-3xl mx-auto" style={{ color: '#606070' }}>
            A complete pre-registration toolkit.
          </p>
          <div className="space-y-10">
            {deliverables.map((d, i) => (
              <article
                key={i}
                className="rounded-3xl p-8 md:p-12 shadow-md hover:shadow-xl transition-shadow"
                style={{ backgroundColor: 'white' }}
              >
                <div className="grid md:grid-cols-[auto_1fr] gap-8 items-start">
                  <div className="flex md:flex-col items-start gap-4 md:gap-3">
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: '#606090' }}
                    >
                      <d.icon className="h-8 w-8 text-white" aria-hidden="true" />
                    </div>
                    <div className="text-4xl font-bold" style={{ color: '#606090', opacity: 0.5 }}>
                      0{i + 1}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold mb-4" style={{ color: '#303040' }}>
                      {d.title}
                    </h3>
                    <p className="text-base md:text-lg leading-relaxed mb-6" style={{ color: '#606070' }}>
                      {d.lede}
                    </p>
                    <div className="space-y-2">
                      {d.bullets.map((b, bi) => (
                        <div key={bi} className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 mt-1 flex-shrink-0" style={{ color: '#606090' }} aria-hidden="true" />
                          <span className="text-base" style={{ color: '#505060' }}>
                            {b}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(96, 96, 144, 0.08)' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-16 text-center" style={{ color: '#303040' }}>
            Process
          </h2>
          <ol className="space-y-6">
            {processSteps.map((step, i) => (
              <li key={i} className="flex items-start gap-6 p-6 rounded-2xl" style={{ backgroundColor: 'white' }}>
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white text-lg"
                  style={{ backgroundColor: '#606090' }}
                  aria-hidden="true"
                >
                  {i + 1}
                </div>
                <p className="text-base md:text-lg leading-relaxed pt-2" style={{ color: '#505060' }}>
                  {step}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Ideal For */}
      <section className="py-24 px-6" style={{ backgroundColor: 'white' }}>
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-8">
            <Users className="h-8 w-8" style={{ color: '#606090' }} aria-hidden="true" />
            <h2 className="text-3xl md:text-4xl font-bold" style={{ color: '#303040' }}>
              Ideal For
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {idealFor.map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-6 rounded-xl" style={{ backgroundColor: '#F8F4EE' }}>
                <CheckCircle2 className="h-6 w-6 mt-0.5 flex-shrink-0" style={{ color: '#606090' }} aria-hidden="true" />
                <span className="text-base md:text-lg" style={{ color: '#505060' }}>
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why This Matters */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="p-8 md:p-12 rounded-3xl" style={{ backgroundColor: 'rgba(96, 96, 144, 0.12)' }}>
            <div className="flex items-center gap-3 mb-6">
              <Award className="h-7 w-7" style={{ color: '#606090' }} aria-hidden="true" />
              <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#606090' }}>
                Why This Matters
              </h2>
            </div>
            <p className="text-lg md:text-xl leading-relaxed" style={{ color: '#505060' }}>
              This pack saves three to six months of rework, prevents regulator pushback, and ensures you start with the right products in the right countries. A perfect dossier means little if it is built for the wrong market. Groundwork makes sure it never is.
            </p>
          </div>
        </div>
      </section>

      {/* Retainer CTA Block */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(160, 96, 176, 0.08)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="rounded-3xl p-8 md:p-12 shadow-xl" style={{ backgroundColor: 'white' }}>
            <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
              <div
                className="w-20 h-20 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: '#A060B0' }}
              >
                <Users className="h-10 w-10 text-white" aria-hidden="true" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: '#303040' }}>
                  Want your entire regulatory workflow handled continuously?
                </h2>
                <p className="text-base md:text-lg leading-relaxed mb-6" style={{ color: '#708080' }}>
                  Once your markets are chosen, the <strong>Apostrophe Retainer</strong> gives you one dedicated regulatory affairs expert to carry the plan through every stage on an ongoing basis, so you never have to hire, train, or run an in-house regulatory team.
                </p>
                <Link to="/retainer">
                  <Button
                    size="lg"
                    className="px-6 py-5 rounded-full transition-all duration-300 hover:scale-105"
                    style={{ backgroundColor: '#A060B0', color: 'white' }}
                    data-testid="groundwork-retainer-btn"
                  >
                    Explore the Retainer Plan
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(96, 96, 144, 0.08)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: '#303040' }}>
            Ready to Build Your Foundation?
          </h2>
          <p className="text-lg mb-10" style={{ color: '#606070' }}>
            Share your product list and we will map the right markets and SKUs for your launch.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact#send-us-your-details">
              <Button
                size="lg"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: '#606090', color: 'white' }}
                data-testid="groundwork-cta-build-btn"
              >
                Build My Groundwork Pack
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact#send-us-your-details">
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ borderColor: '#606090', color: '#606090' }}
                data-testid="groundwork-cta-upload-btn"
              >
                Upload Product List
                <Upload className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ - AEO */}
      <section className="py-24 px-6" style={{ backgroundColor: 'white' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center" style={{ color: '#303040' }}>
            Frequently Asked Questions
          </h2>
          <p className="text-center text-base md:text-lg mb-16" style={{ color: '#708080' }}>
            Straight answers about country selection, SKU prioritization and market-entry planning.
          </p>
          <div className="space-y-6">
            {faqs.map((f, i) => (
              <details
                key={i}
                className="group rounded-2xl p-6 md:p-8 transition-shadow hover:shadow-md"
                style={{ backgroundColor: '#F8F4EE' }}
                open={i === 0}
              >
                <summary
                  className="cursor-pointer list-none flex items-start justify-between gap-4"
                  data-testid={`groundwork-faq-${i}`}
                >
                  <h3 className="text-lg md:text-xl font-bold" style={{ color: '#303040' }}>
                    {f.q}
                  </h3>
                  <span
                    className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center transition-transform group-open:rotate-45"
                    style={{ backgroundColor: '#606090', color: 'white' }}
                    aria-hidden="true"
                  >
                    +
                  </span>
                </summary>
                <p className="text-base md:text-lg leading-relaxed mt-4" style={{ color: '#606070' }}>
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
